import { useRoute, navigateTo } from 'nuxt/app';
import type { RouteLocationRaw } from 'vue-router';
import { permissions } from '~/constants';
import type { SessionsConfig, SessionsUserInfo } from '@/types';

export const setSessionToken = (data: { token: string }) => {
  localStorage.setItem('sessionToken', `Bearer ${data.token}`);
};

export const setValidationConfiguration = (validationConfiguration: any) => {
  try {
    localStorage.setItem('validationConfiguration', JSON.stringify(validationConfiguration));
  } catch {
    localStorage.setItem('validationConfiguration', 'null');
  }
};

export const setPartnerInfo = (data: { config: SessionsConfig }) => {
  const partnerData = data.config;

  localStorage.setItem('partnerId', partnerData.IdCompany || '');
  localStorage.setItem('partnerCompanyName', partnerData.ComName || '');
  localStorage.setItem('partnerName', partnerData.PartnerName || '');
  localStorage.setItem('partnerCountry', partnerData.Country || '');
  localStorage.setItem('partnerCurrency', partnerData.currency || 'EUR');
  localStorage.setItem('partnerActivity', partnerData.ActitvityType || '');
  localStorage.setItem('envId', partnerData.IdCompany || '');
  localStorage.setItem('hasBankFees', partnerData.ShowBankFees || '');
  localStorage.setItem('hasMoneyOutId', partnerData.ShowMoneyOutIdforP2P || '');
  localStorage.setItem('disableMoneyOut', partnerData.DisableMoneyOutButtonFromBo || '');
  localStorage.setItem('createdAt', partnerData.DtStart || '');
  localStorage.setItem('sla', partnerData.SLA || '');
  localStorage.setItem('exportLimit', partnerData.LimitLineExport || '0');
  localStorage.setItem(
    'disableAccountlessWalletCreation',
    partnerData.DisableAccountlessWalletCreation?.toString() || 'false'
  );
  setValidationConfiguration(partnerData.ValidationConfiguration);
  localStorage.setItem('brandingRepositoryUrl', partnerData.BrandingRepositoryUrl || '');
  localStorage.setItem(
    'forceOnlineOnboardingForWalletCreation',
    partnerData.ForceOnlineOnboardingForWalletCreation?.toString() || 'false'
  );
  localStorage.setItem(
    'isDueDiligenceRequired',
    partnerData.isDueDiligenceRequired?.toString() || 'false'
  );
};

export const setUserInfo = (data: { UserInfo: SessionsUserInfo }) => {
  const userInfo = data.UserInfo;
  localStorage.setItem('userId', userInfo.UserId || '');
  localStorage.setItem('username', userInfo.Username || '');
  localStorage.setItem('firstname', userInfo.FirstName || '');
  localStorage.setItem('email', userInfo.Email || '');

  let name = '';
  if (userInfo.FirstName) {
    name += userInfo.FirstName;
  }
  if (userInfo.FirstName && userInfo.LastName) {
    name += ' ';
  }
  if (userInfo.LastName) {
    name += userInfo.LastName;
  }
  localStorage.setItem('name', name);

  localStorage.setItem('roleId', userInfo.ProfileId || '');
  localStorage.setItem('roleName', userInfo.ProfileName || '');
  localStorage.setItem('userCreatedAt', userInfo.CreateDate || '');

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('jimo:identify'));
  }
};

export const setUserPermissions = (data: { permissions: number[] }) => {
  localStorage.setItem('permissions', JSON.stringify(data.permissions));
  localStorage.setItem(
    'permissionNames',
    JSON.stringify(
      permissions
        .filter((permission) => data.permissions.includes(permission.value))
        .map((permission) => permission.text)
    )
  );
};

export const redirectAfterSignIn = async () => {
  const router = useRouter();
  const route = useRoute();

  const homePage = {
    name: 'partner-home',
    params: { partner: route.params.partner }
  };

  const lastVisited = route.query && route.query.redirect;

  if (lastVisited && typeof lastVisited === 'string') {
    await router.replace(lastVisited);
  } else {
    await router.replace(homePage);
  }

  document.location.reload();
};

const clearLocalStorage = () => {
  const runtimeConfig = useConfig();
  const headwayId = runtimeConfig.value.public.headwayId;

  const toBeRemovedKeys = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index) as string;
    if (
      !['userLocale', `HW_readItems[${headwayId}]`, `HW_seenItems[${headwayId}]`, 'theme'].includes(
        key
      ) &&
      !key?.startsWith('state-storage-')
    ) {
      toBeRemovedKeys.push(localStorage.key(index));
    }
  }

  toBeRemovedKeys.forEach((key) => {
    if (typeof key === 'string') {
      localStorage.removeItem(key);
    }
  });

  let stateStorage;
  for (const key in localStorage) {
    try {
      if (key.startsWith('state-storage-') && !key.endsWith('shortcut')) {
        const item = localStorage.getItem(key);
        if (item !== null) {
          stateStorage = JSON.parse(item);
        }

        delete stateStorage.filterValue;
        delete stateStorage.searchText;
        if (stateStorage.columns) {
          stateStorage.columns.forEach(
            (column: { filterValue?: string; filterValues?: string; searchText?: string }) => {
              delete column.filterValue;
              delete column.filterValues;
            }
          );
        }

        localStorage.setItem(key, JSON.stringify(stateStorage));
      }
    } catch (error) {
      console.error(error);
    }
  }
};

const redirectToSignIn = () => {
  const router = useRouter();
  const route = useRoute();
  const signInPage: RouteLocationRaw = {
    name: 'partner-sign-in',
    params: { partner: route.params.partner }
  };

  const currentRoute = router.currentRoute.value;
  const lastVisited = currentRoute.fullPath.split('/').slice(2).join('/');
  const isNotAuthPage =
    currentRoute.name !== 'partner-sign-in' &&
    currentRoute.name !== 'partner-sign-up' &&
    currentRoute.name !== 'partner-reset-password';

  if (lastVisited && isNotAuthPage) {
    signInPage.query = { redirect: lastVisited };
  }

  navigateTo(signInPage);
};

const stopWootric = () => {
  if (useConfig().value.public.lemonwayEnv.toLowerCase() === 'production') {
    window.WootricSurvey.stop();
  }
};

export const signOut = () => {
  clearLocalStorage();
  stopWootric();
  redirectToSignIn();
};

export const checkPermission = (action: string) => {
  const permissionNames = localStorage.getItem('permissionNames');
  return permissionNames !== null && JSON.parse(permissionNames).includes(action);
};

export const checkNavigationPermission = (action: string) => {
  if (action === 'checkRequest') {
    const validationList = localStorage.getItem('validationConfiguration');
    return (
      validationList !== null &&
      validationList !== 'null' &&
      Object.keys(JSON.parse(validationList)).includes('moneyOut') &&
      checkPermission('manageOperations')
    );
  } else if (action === 'pre-accounts') {
    const forceOOFlag = localStorage.getItem('forceOnlineOnboardingForWalletCreation');
    return forceOOFlag !== null && forceOOFlag !== 'null' && forceOOFlag === 'true';
  }
  return checkPermission(action);
};
